import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { CategorySubCategorySelect } from "@/components/category-sub-category-select/CategorySubCategorySelect";
import { budgetItemSchema, BudgetItemInput } from "./budgetItemSchema";
import { Plus } from "lucide-react";
import { useCurrencyList } from "@/hooks/useCurrencyList";

interface BudgetItemFormRowProps {
  onAdd: (item: BudgetItemInput) => Promise<void>;
  isAdding: boolean;
}

export function BudgetItemFormRow({ onAdd, isAdding }: BudgetItemFormRowProps) {
  const form = useForm<BudgetItemInput>({
    resolver: zodResolver(budgetItemSchema),
    defaultValues: {
      category_id: "",
      sub_category_id: "",
      amount: 0,
      currency: "USD",
      description: "",
    },
  });

  const {
    data: availableCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorLoadingCurrencies,
  } = useCurrencyList();

  const onSubmit = async (data: BudgetItemInput) => {
    await onAdd(data);
    form.reset();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
        <FormField
          control={form.control}
          name="category_id"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <CategorySubCategorySelect
                  categoryId={field.value}
                  subCategoryId={form.watch("sub_category_id")}
                  onCategoryChange={(id) => {
                    form.setValue("category_id", id, { shouldValidate: true });
                  }}
                  onSubCategoryChange={(id) => {
                    form.setValue("sub_category_id", id, {
                      shouldValidate: true,
                    });
                  }}
                  disabled={isAdding}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* sub_category_id errors surfaced separately */}
        {form.formState.errors.sub_category_id && (
          <p className="text-sm font-medium text-destructive">
            {form.formState.errors.sub_category_id.message}
          </p>
        )}

        <div className="flex gap-2">
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <Input
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="Amount"
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseFloat(e.target.value) || 0)
                    }
                    disabled={isAdding}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem className="w-28">
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={isAdding}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Currency" />
                    </SelectTrigger>
                  </FormControl>
                  {isLoadingCurrencies && "Loading Currencies"}
                  {!isLoadingCurrencies && !errorLoadingCurrencies ? (
                    <SelectContent>
                      {availableCurrencies.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  ) : (
                    "Failed to load currencies"
                  )}
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder="Description (optional)"
                  {...field}
                  disabled={isAdding}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" disabled={isAdding} className="w-full">
          <Plus className="mr-2 h-4 w-4" />
          {isAdding ? "Adding..." : "Add Item"}
        </Button>
      </form>
    </Form>
  );
}
